import { useMemo } from "react";
import { useSubscriptionProducts } from "./useSubscriptionProducts";
import { SubscriptionPlan } from "enums/subscription-plans";
import { getPlanBasePrices } from "helpers/products";
import { StripePrice } from "../types";
import useSubscription from "features/User/hooks/useSubscription";

export type PlanPricing = {
  baseMonthlyPrice: StripePrice | null;
  baseYearlyPrice: StripePrice | null;
  actualMonthlyPrice: StripePrice | null;
  actualYearlyPrice: StripePrice | null;
  isGrandfathered: boolean;
  getActivePrice: (isAnnual: boolean) => StripePrice | null;
};

/**
 * Custom hook for handling subscription pricing including grandfathered rates
 *
 * @param plan - The subscription plan to get pricing for
 * @returns {PlanPricing} Object containing:
 *   - baseMonthlyPrice: Standard monthly price from products
 *   - baseYearlyPrice: Standard yearly price from products
 *   - actualMonthlyPrice: Actual price (may be grandfathered)
 *   - actualYearlyPrice: Actual price (may be grandfathered)
 *   - isGrandfathered: Whether user has grandfathered pricing
 *   - getActivePrice: Helper to get price based on billing interval
 */
export const useSubscriptionPricing = (plan: SubscriptionPlan): PlanPricing => {
  const { products } = useSubscriptionProducts();
  const { subscription } = useSubscription();

  return useMemo(() => {
    // Get base prices from products
    const { baseMonthlyPrice, baseYearlyPrice } = getPlanBasePrices(
      products,
      plan
    );

    // Check for grandfathered pricing from existing subscription
    let actualMonthlyPrice = baseMonthlyPrice;
    let actualYearlyPrice = baseYearlyPrice;
    let isGrandfathered = false;

    // Extract grandfathered price if user has an active subscription
    if (subscription?.user?.items?.data?.[0]?.price) {
      const subscriptionPrice = subscription.user.items.data[0].price;
      const subscriptionInterval = subscriptionPrice.recurring?.interval;

      // Check if this is a grandfathered price (different from current product prices)
      if (subscriptionInterval === "month" && baseMonthlyPrice?.unit_amount) {
        if (subscriptionPrice.unit_amount !== baseMonthlyPrice.unit_amount) {
          actualMonthlyPrice = subscriptionPrice;
          isGrandfathered = true;
        }
      } else if (
        subscriptionInterval === "year" &&
        baseYearlyPrice?.unit_amount
      ) {
        if (subscriptionPrice.unit_amount !== baseYearlyPrice.unit_amount) {
          actualYearlyPrice = subscriptionPrice;
          isGrandfathered = true;
        }
      }
    }

    // Helper function to get the active price based on billing interval
    const getActivePrice = (isAnnual: boolean) => {
      return isAnnual ? actualYearlyPrice : actualMonthlyPrice;
    };

    return {
      baseMonthlyPrice,
      baseYearlyPrice,
      actualMonthlyPrice,
      actualYearlyPrice,
      isGrandfathered,
      getActivePrice,
    };
  }, [products, subscription, plan]);
};
